import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../../components/design-system';
import { HandbookView } from '../../components/handbook/HandbookView';
import { AdminPage } from './AdminPage';
import type { HandbookContent } from '../../types';

interface MatchInfo {
  status: string;
  traveler_name: string | null;
  host_name: string | null;
  host_avatar: string | null;
}

function isHandbook(c: unknown): c is HandbookContent {
  return !!c && typeof c === 'object' && 'days' in (c as object);
}

/**
 * Read-only admin "eye" preview — renders exactly what the host sees for
 * this trip (via the same HandbookView the host and traveler already share),
 * without requiring the admin to actually become that host. No edit/accept
 * controls at all; this is look-don't-touch, by design (host reassignment
 * and everything else destructive lives back on the trips table itself,
 * where it's explicit and logged, not tucked inside a "preview").
 */
export function AdminTripPreview() {
  const { id: matchId } = useParams<{ id: string }>();
  const [match, setMatch] = useState<MatchInfo | null>(null);
  const [handbook, setHandbook] = useState<HandbookContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!matchId) return;
    void (async () => {
      const { data: m } = await supabase
        .from('matches')
        .select(`
          status,
          traveler:profiles!matches_traveler_id_fkey(full_name),
          host:profiles!matches_host_id_fkey(full_name, avatar_url)
        `)
        .eq('id', matchId)
        .maybeSingle();

      if (!m) { setNotFound(true); setLoading(false); return; }
      const raw = m as unknown as Record<string, unknown>;
      const host = raw.host as { full_name: string | null; avatar_url: string | null } | null;
      setMatch({
        status: raw.status as string,
        traveler_name: (raw.traveler as { full_name: string | null } | null)?.full_name ?? null,
        host_name: host?.full_name ?? null,
        host_avatar: host?.avatar_url ?? null,
      });

      const { data: itin } = await supabase
        .from('itineraries')
        .select('content')
        .eq('match_id', matchId)
        .maybeSingle();

      if (itin && isHandbook(itin.content)) setHandbook(itin.content);
      setLoading(false);
    })();
  }, [matchId]);

  if (loading) {
    return (
      <div className="min-h-dvh bg-app-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (notFound || !match) {
    return (
      <AdminPage title="Trip preview">
        <p className="text-sm text-app-muted">Trip not found.</p>
        <Link to="/admin/trips" className="text-sm text-gold mt-3 inline-block">Back to trips</Link>
      </AdminPage>
    );
  }

  return (
    <div className="min-h-dvh bg-app-bg">
      <div className="px-6 py-4 border-b border-app-border flex items-center justify-between gap-3 sticky top-0 bg-app-bg z-10">
        <div className="flex items-center gap-2 text-sm">
          <Link to="/admin/trips" className="text-app-muted hover:text-app-fg transition-colors">
            <i className="ti ti-arrow-left" /> Trips
          </Link>
          <span className="text-app-border">/</span>
          <span className="text-app-fg font-medium">
            {match.traveler_name ?? 'Traveler'} · {match.host_name ?? 'Unassigned host'}
          </span>
          <span className="text-[11px] uppercase tracking-wide text-app-muted border border-app-border rounded-full px-2 py-0.5 ml-1">
            {match.status}
          </span>
        </div>
        <span className="text-[11px] text-app-muted flex items-center gap-1.5">
          <i className="ti ti-eye" /> Admin view — read-only, this host cannot see you're looking
        </span>
      </div>

      {!handbook ? (
        <div className="px-6 py-16 text-center text-sm text-app-muted">
          This host hasn't started a handbook for this trip yet.
        </div>
      ) : (
        <HandbookView handbook={handbook} hostFirstName={match.host_name?.split(' ')[0] ?? 'Host'} hostAvatarUrl={match.host_avatar} />
      )}
    </div>
  );
}

export default AdminTripPreview;
